import { useState } from "react";
import { Link, Location } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import classNames from "classnames";
import { getUserFullName } from "services/users/utils";
import {
  favoriteStudentsQuery,
  removeStudentFromFavorites,
  FavoriteStudent,
} from "services/favorites";
import { lastLessonPerStudentQuery } from "services/sessions/lesson";
import { routes } from "pages/routes";
import Action from "components/formElements/Action/Action";
import TalaeraSpinner from "components/TalaeraSpinner/TalaeraSpinner";
import trashIcon from "images/trash.svg";
import ConfirmDialog from "components/ui/Dialog/ConfirmDialog";
import { useUser } from "contexts/UserContext";
import {
  daysSinceEngagement,
  groupStudents,
  isPrimaryInstructor,
  needsLastLessonLookup,
  STUDENT_GROUP_ORDER,
  STUDENT_GROUP_TITLES,
  StudentGroupName,
} from "./studentGroups";
import styles from "./StudentListSidePanel.module.scss";

type Props = {
  location: Location;
};

export default function StudentListSidePanel(props: Props) {
  const sessionUser = useUser();
  const queryClient = useQueryClient();

  const [showPastStudents, setShowPastStudents] = useState(false);

  const favoritesResult = useQuery(favoriteStudentsQuery());
  const students = favoritesResult.data?.list ?? [];

  // Only students who already look dormant by last_seen can be rescued by a recent
  // lesson, so most teachers never pay for this request at all.
  const lastLessonsResult = useQuery({
    ...lastLessonPerStudentQuery(),
    enabled: needsLastLessonLookup(students),
  });

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: favoriteStudentsQuery.queryKeyPrefix });
  };

  const togglePastStudents = () => {
    setShowPastStudents(shown => !shown);
  };

  if (favoritesResult.isPending) {
    return (
      <div className={styles.panel}>
        <TalaeraSpinner />
      </div>
    );
  }

  const groups = groupStudents({
    students,
    instructorId: sessionUser.id,
    lastLessonByStudent: lastLessonsResult.data,
  });
  const pastStudents = groups.past;

  return (
    <div className={styles.panel}>
      {STUDENT_GROUP_ORDER.map(group => (
        <StudentGroup
          key={group}
          name={group}
          students={groups[group]}
          location={props.location}
          instructorId={sessionUser.id}
          lastLessonByStudent={lastLessonsResult.data}
          onRemoved={refresh}
        />
      ))}

      {pastStudents.length > 0 && (
        <div className={styles.pastGroup}>
          <button
            type="button"
            className={styles.pastToggle}
            aria-expanded={showPastStudents}
            onClick={togglePastStudents}
          >
            <span className={styles.caret} aria-hidden="true">
              {showPastStudents ? "▾" : "▸"}
            </span>
            {STUDENT_GROUP_TITLES.past}
            <span className={styles.count}>{pastStudents.length}</span>
          </button>
          {showPastStudents && (
            <ul className={styles.list}>
              {pastStudents.map(student => (
                <StudentListItem
                  key={student.id}
                  student={student}
                  location={props.location}
                  isCovering={!isPrimaryInstructor(student, sessionUser.id)}
                  quietDays={null}
                  onRemoved={refresh}
                />
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

type StudentGroupProps = {
  name: StudentGroupName;
  students: FavoriteStudent[];
  location: Location;
  instructorId: string;
  lastLessonByStudent?: Record<string, string>;
  onRemoved: () => void;
};

function StudentGroup(props: StudentGroupProps) {
  if (props.students.length === 0) {
    return null;
  }

  return (
    <div className={styles.group}>
      <h4 className={styles.groupTitle}>
        {STUDENT_GROUP_TITLES[props.name]}
        <span className={styles.count}>{props.students.length}</span>
      </h4>
      <ul className={styles.list}>
        {props.students.map(student => (
          <StudentListItem
            key={student.id}
            student={student}
            location={props.location}
            isCovering={!isPrimaryInstructor(student, props.instructorId)}
            quietDays={
              props.name === "quiet"
                ? daysSinceEngagement(student, props.lastLessonByStudent)
                : null
            }
            onRemoved={props.onRemoved}
          />
        ))}
      </ul>
    </div>
  );
}

type StudentListItemProps = {
  student: FavoriteStudent;
  location: Location;
  isCovering: boolean;
  quietDays: number | null;
  onRemoved: () => void;
};

function StudentListItem(props: StudentListItemProps) {
  const [showConfirmation, setShowConfirmation] = useState(false);

  const path = routes.studentDetails.path({ id: props.student.id });
  const name = getUserFullName(props.student.profile);

  const openConfirmation = () => {
    setShowConfirmation(true);
  };

  const closeConfirmation = () => {
    setShowConfirmation(false);
  };

  return (
    <li
      className={classNames(styles.item, {
        [styles.current]: isCurrentStudentPath(path, props.location.pathname),
      })}
    >
      <Link className={styles.link} to={path}>
        {name}
      </Link>
      {props.quietDays !== null && (
        <span
          className={styles.quiet}
          title={`No login or lesson for ${props.quietDays} days`}
        >
          {formatQuietDays(props.quietDays)}
        </span>
      )}
      {props.isCovering && (
        <span
          className={styles.covering}
          title="You are covering for another instructor"
          aria-label="Covering for another instructor"
        />
      )}
      {props.isCovering && (
        <Action
          className={styles.remove}
          onClick={openConfirmation}
          aria-label={`Remove ${name} from your list`}
          title={`Remove ${name} from your list`}
        >
          <img className={styles.removeIcon} src={trashIcon} alt="" />
        </Action>
      )}
      {showConfirmation && (
        <ConfirmDialog
          title="Remove from your list?"
          confirmLabel="Remove"
          danger
          message={`Remove ${name} from the students you are covering for?`}
          onConfirm={() => removeStudentFromFavorites(props.student.id)}
          onClose={removed => {
            closeConfirmation();
            if (removed) {
              props.onRemoved();
            }
          }}
        />
      )}
    </li>
  );
}

/**
 * The student route carries an optional tab segment, so `/student/42/lessons` has to
 * keep the `/student/42` row highlighted.
 */
function isCurrentStudentPath(studentPath: string, pathname: string) {
  return pathname === studentPath || pathname.startsWith(`${studentPath}/`);
}

function formatQuietDays(days: number) {
  return days >= 365 ? `${Math.floor(days / 30)}mo` : `${days}d`;
}
